import React, { useState, useEffect } from 'react';
import { Users, Building2, ShoppingBag, Crown, Ban, CheckCircle, RefreshCw, LogOut, AlertTriangle } from 'lucide-react';
import { clearJwt } from '../utils/utils.js';

// Must mirror PLANS in BillingPanel.jsx and planLimits() in api/tenant.php
const PLAN_PRICES = { trial: 0, starter: 4000, growth: 6000, enterprise: 8000 };
const PLAN_COLORS = {
    trial:      'bg-[#f6f7f8] text-[#5c5f62]',
    starter:    'bg-[#e4fcf4] text-[#008060]',
    growth:     'bg-[#e3d0ff] text-[#50248f]',
    enterprise: 'bg-[#fff5ea] text-[#b98900]',
};

const SuperAdminPanel = ({ apiCall, showToast, onLogout }) => {
    const [tenants, setTenants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState(null);

    const load = async () => {
        setLoading(true);
        const res = await apiCall('getTenants', {});
        if (Array.isArray(res)) setTenants(res);
        else showToast(res?.error || 'Failed to load tenants', 'error');
        setLoading(false);
    };

    useEffect(() => { load(); }, []);

    const setPlan = async (tid, plan) => {
        setUpdating(tid + '_plan');
        await apiCall('updateTenantPlan', { tenant_id: tid, plan });
        showToast(`Plan updated to ${plan}`);
        await load();
        setUpdating(null);
    };

    const setStatus = async (tid, status) => {
        setUpdating(tid + '_status');
        await apiCall('updateTenantStatus', { tenant_id: tid, status });
        showToast(`Tenant ${status}`);
        await load();
        setUpdating(null);
    };

    // MRR calculation
    const mrr = tenants
        .filter(t => t.status === 'active' && t.plan !== 'trial')
        .reduce((sum, t) => sum + (PLAN_PRICES[t.plan] || 0), 0);

    const activePaid  = tenants.filter(t => t.status === 'active' && t.plan !== 'trial').length;
    const trialCount  = tenants.filter(t => t.plan === 'trial').length;
    const suspended   = tenants.filter(t => t.status === 'suspended').length;

    return (
        <div className="min-h-screen bg-[#1a1a1a] text-white p-6">
            <div className="max-w-6xl mx-auto space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#b98900] rounded-xl flex items-center justify-center">
                            <Crown size={20} className="text-white"/>
                        </div>
                        <div>
                            <h1 className="text-xl font-black">Super Admin</h1>
                            <p className="text-xs text-[#5c5f62]">Tenant Management</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={load} disabled={loading} className="flex items-center gap-2 bg-[#303030] hover:bg-[#404040] text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50">
                            <RefreshCw size={14} className={loading ? 'animate-spin' : ''}/> Refresh
                        </button>
                        <button onClick={() => { clearJwt(); onLogout(); }} className="flex items-center gap-2 bg-[#d82c0d] hover:bg-[#b82000] text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors">
                            <LogOut size={14}/> Logout
                        </button>
                    </div>
                </div>

                {/* MRR KPI row */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    {[
                        { label: 'Monthly Recurring Revenue', value: `Rs ${mrr.toLocaleString('en-PK')}`, accent: '#b98900' },
                        { label: 'Active Paid Tenants',       value: activePaid, accent: '#008060' },
                        { label: 'On Trial',                  value: trialCount, accent: '#5c5f62' },
                        { label: 'Suspended',                 value: suspended,  accent: '#d82c0d' },
                    ].map(({ label, value, accent }) => (
                        <div key={label} className="bg-[#242424] rounded-xl p-4 border border-[#303030]">
                            <p className="text-[10px] text-[#5c5f62] uppercase font-bold tracking-wide">{label}</p>
                            <p className="text-2xl font-black mt-1" style={{ color: accent }}>{value}</p>
                        </div>
                    ))}
                </div>

                {/* Tenant table */}
                <div className="bg-[#1e1e1e] rounded-xl border border-[#303030] overflow-hidden">
                    <div className="p-4 border-b border-[#303030] bg-[#242424]">
                        <h2 className="font-bold text-sm">All Tenants ({tenants.length})</h2>
                    </div>
                    <div className="overflow-x-auto">
                        {loading ? (
                            <div className="p-12 text-center text-[#5c5f62]">Loading tenants…</div>
                        ) : tenants.length === 0 ? (
                            <div className="p-12 text-center text-[#5c5f62]">No tenants yet.</div>
                        ) : (
                            <table className="w-full text-sm">
                                <thead className="border-b border-[#303030] text-[#5c5f62] text-xs">
                                    <tr>
                                        <th className="p-4 text-left font-bold uppercase">Tenant</th>
                                        <th className="p-4 text-center font-bold uppercase">Plan</th>
                                        <th className="p-4 text-center font-bold uppercase">Status</th>
                                        <th className="p-4 text-center font-bold uppercase">Branches</th>
                                        <th className="p-4 text-center font-bold uppercase">Staff</th>
                                        <th className="p-4 text-center font-bold uppercase">Orders</th>
                                        <th className="p-4 text-center font-bold uppercase">Joined</th>
                                        <th className="p-4 text-right font-bold uppercase">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-[#2a2a2a]">
                                    {tenants.map(t => (
                                        <tr key={t.id} className="hover:bg-[#242424] transition-colors">
                                            <td className="p-4">
                                                <div className="font-semibold text-white">{t.name || t.id}</div>
                                                <div className="text-[10px] text-[#5c5f62] mt-0.5">{t.email || '—'}</div>
                                                <div className="text-[10px] text-[#444] font-mono">{t.id}</div>
                                            </td>
                                            <td className="p-4 text-center">
                                                <select
                                                    value={t.plan || 'trial'}
                                                    disabled={updating === t.id + '_plan'}
                                                    onChange={e => setPlan(t.id, e.target.value)}
                                                    className={`text-[10px] font-bold px-2 py-1 rounded-lg border-0 cursor-pointer ${PLAN_COLORS[t.plan] || PLAN_COLORS.trial}`}
                                                >
                                                    {Object.keys(PLAN_PRICES).map(p => (
                                                        <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>
                                                    ))}
                                                </select>
                                            </td>
                                            <td className="p-4 text-center">
                                                <span className={`px-2 py-1 rounded-lg text-[10px] font-bold ${
                                                    t.status === 'active' ? 'bg-[#e4fcf4] text-[#008060]' :
                                                    t.status === 'suspended' ? 'bg-[#fbeae5] text-[#d82c0d]' :
                                                    'bg-[#f6f7f8] text-[#5c5f62]'
                                                }`}>{t.status || 'active'}</span>
                                            </td>
                                            <td className="p-4 text-center text-[#9a9a9a]">{t.branch_count ?? 0}</td>
                                            <td className="p-4 text-center text-[#9a9a9a]">{t.staff_count ?? 0}</td>
                                            <td className="p-4 text-center text-[#9a9a9a]">{t.order_count ?? 0}</td>
                                            <td className="p-4 text-center text-[#5c5f62] text-xs">{t.created_at?.slice(0,10) || '—'}</td>
                                            <td className="p-4 text-right">
                                                <div className="flex items-center justify-end gap-1">
                                                    {t.status !== 'active' ? (
                                                        <button
                                                            onClick={() => setStatus(t.id, 'active')}
                                                            disabled={updating === t.id + '_status'}
                                                            className="flex items-center gap-1 text-[10px] font-bold px-2 py-1.5 bg-[#e4fcf4] text-[#008060] rounded-lg hover:bg-[#008060] hover:text-white transition-colors disabled:opacity-50"
                                                        ><CheckCircle size={11}/> Activate</button>
                                                    ) : (
                                                        <button
                                                            onClick={() => setStatus(t.id, 'suspended')}
                                                            disabled={updating === t.id + '_status'}
                                                            className="flex items-center gap-1 text-[10px] font-bold px-2 py-1.5 bg-[#fbeae5] text-[#d82c0d] rounded-lg hover:bg-[#d82c0d] hover:text-white transition-colors disabled:opacity-50"
                                                        ><Ban size={11}/> Suspend</button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>

                {/* Payment proofs pending */}
                <div className="bg-[#1e1e1e] rounded-xl border border-[#b98900]/30 p-4">
                    <div className="flex items-center gap-2 text-[#b98900] mb-2">
                        <AlertTriangle size={16}/>
                        <span className="font-bold text-sm">Pending Payment Proofs</span>
                    </div>
                    <p className="text-xs text-[#5c5f62]">Check the Audit Log in each tenant's Settings page for <code className="bg-[#303030] px-1 rounded">payment_proof_submitted</code> entries. Activate the tenant by changing their plan above after verifying payment.</p>
                </div>
            </div>
        </div>
    );
};

export default SuperAdminPanel;
